frappe.provide("frappe.search");
import { fuzzy_match, character_compare_i18n } from "./fuzzy_match.js";

// @dokos: results within this fraction of the best match are ordered by history instead.
const FRECENCY_BAND = 0.7;
// fuzzy_match centres scores on 100; a band below it makes almost everything a near-tie.
const FUZZY_BASE_SCORE = 100;
// Separates duplicate routes, whose ties Awesomplete's stable sort would keep.
const RANK_EPSILON = 1e-6;

// @dokos: per-query memory of the awesome bar picks (frappe.search.memory below)
const MEMORY_KEY = "awesomebar_selections";
// Digit-only keys would iterate first and break the LRU eviction.
const MEMORY_KEY_PREFIX = "q:";
const MEMORY_MAX_QUERIES = 100;
// One pick is enough to pin; the decay is what makes that safe.
const MEMORY_MIN_CONFIDENCE = 0.65;
const MEMORY_HALF_LIFE_DAYS = 14;

// @dokos: scoring key for drafts (visit_key in route_history.py), not a route.
const NEW_DOCUMENT_KEY_PREFIX = "New/";

frappe.search.utils = {
	setup_recent: function () {
		this.recent = JSON.parse(frappe.boot.user.recent || "[]") || [];
	},
	make_icon(name) {
		// @dokos
		return `<span style="padding: 0 3px; margin-right: 5px;">${frappe.utils.icon(
			name
		)}</span>`;
	},
	results_to_hide: [],
	get_recent_pages: function (keywords) {
		if (keywords === null) keywords = "";
		var me = this,
			values = [],
			options = [];

		function find(list, keywords, process) {
			list.forEach(function (item, i) {
				var _item = $.isArray(item) ? item[0] : item;
				_item = __(_item || "")
					.toLowerCase()
					.replace(/-/g, " ");

				if (keywords === "" || keywords === _item || _item.indexOf(keywords) !== -1) {
					var option = process(item);

					if (option) {
						if ($.isPlainObject(option)) {
							option = [option];
						}
						option.forEach(function (o) {
							o.match = item;
							o.recent = true;
						});

						options = option.concat(options);
					}
				}
			});
		}

		me.recent.forEach(function (doctype, i) {
			values.push([doctype[1], ["Form", doctype[0], doctype[1]]]);
		});

		values = values.reverse();

		frappe.route_history.forEach(function (route, i) {
			if (route[0] === "Form") {
				values.push([route[2], route]);
			} else if (
				["List", "Tree", "Workspaces", "query-report"].includes(route[0]) ||
				route[2] === "Report"
			) {
				if (route[1]) {
					values.push([route[1], route]);
				}
			} else if (route[0]) {
				values.push([frappe.route_titles[route.join("/")] || route[0], route]);
			}
		});

		find(values, keywords, function (match) {
			const route = match[1];
			const out = { route: route };

			if (route[0] === "Form") {
				const doctype = route[1];
				if (route.length > 2 && doctype !== route[2]) {
					const docname = route[2];
					out.label = me.make_icon("sticky-note") + __(doctype) + " " + docname.bold(); // @dokos
					out.value = __(doctype) + " " + docname;
				} else {
					out.label = me.make_icon("sticky-note") + __(doctype).bold(); // @dokos
					out.value = __(doctype);
				}
			} else if (
				["List", "Tree", "Workspaces", "query-report"].includes(route[0]) &&
				route.length > 1
			) {
				const view_type = route[0];
				const view_name = route[1];

				let icon, labelSuffix; // @dokos

				switch (view_type) {
					case "List":
						icon = me.make_icon("list"); // @dokos
						labelSuffix = __("List");
						break;
					case "Tree":
						icon = me.make_icon("list-tree"); // @dokos
						labelSuffix = __("Tree");
						break;
					case "Workspaces":
						icon = me.make_icon("wallpaper"); // @dokos
						labelSuffix = __("Workspace");
						break;
					case "query-report":
						icon = me.make_icon("table"); // @dokos
						labelSuffix = __("Report");
						break;
				}

				out.label = icon + __(view_name.bold()) + " " + labelSuffix; // @dokos
				out.value = __(view_name) + " " + labelSuffix;
			} else if (match[0]) {
				out.label =
					me.make_icon(me.recent ? "circle-dashed" : "file-text") + // @dokos
					frappe.utils.escape_html(match[0]).bold();
				out.value = match[0];
			} else {
				console.log("Illegal match", match);
			}
			out.index = 80;
			return out;
		});
		this.hide_results(options);
		return options;
	},
	hide_results(options) {
		if (this.results_to_hide.length == 0) return;
		this.results_to_hide.forEach((v, i) => {
			options.forEach((o, j) => {
				if (o.value == v) {
					options.splice(j, 1);
				}
			});
		});
	},
	get_frequent_links() {
		// @dokos: ordered by frecency rank instead of the raw visit count
		const links = frappe.search.frecency
			.links()
			.filter((link) => !link.route.startsWith(NEW_DOCUMENT_KEY_PREFIX))
			.slice(0, 5);
		// Rank, not score: stays below recent pages (80) and never hits 0, which reads as unset.
		const options = links.map((link, rank) => {
			const label = frappe.utils.get_route_label(link.route);
			return {
				route: link.route,
				label: label,
				value: label,
				index: links.length - rank,
			};
		});
		if (!options.length) {
			return this.get_recent_pages("");
		}
		return options;
	},

	get_search_in_list: function (keywords) {
		var me = this;
		var out = [];
		if (keywords.split(" ").includes(__("in")) && keywords.slice(-2) !== __("in")) {
			var parts = keywords.split(` ${__("in")} `);
			frappe.boot.user.can_read.forEach(function (item) {
				if (frappe.boot.user.can_search.includes(item)) {
					const search_result = me.fuzzy_search(parts[1], item, true);
					if (search_result.score) {
						out.push({
							type: "In List",
							label: __("Find {0} in {1}", [
								__(parts[0]),
								search_result.marked_string,
							]),
							value: __("Find {0} in {1}", [__(parts[0]), __(item)]),
							route_options: { name: ["like", "%" + parts[0] + "%"] },
							index: 1 + search_result.score,
							route: ["List", item],
						});
					}
				}
			});
		}
		return out;
	},

	get_creatables: function (keywords) {
		const me = this;
		const out = [];
		const firstKeyword = keywords.split(" ")[0]?.trim().toLowerCase() || "";
		const validKeywords = __("new")
			.split(",")
			.map((k) => k.trim());
		validKeywords.push("+");
		if (validKeywords.includes(firstKeyword)) {
			frappe.boot.user.can_create.forEach(function (item) {
				const search = keywords.substring(firstKeyword.length).trim();
				const search_result = me.fuzzy_search(search, item, true);
				const level = search_result.score;
				if (level) {
					out.push({
						type: "New",
						label:
							me.make_icon("circle-plus") + // @dokos
							__("New {0}", [search_result.marked_string || __(item)]),
						value: __("New {0}", [__(item)]),
						index: 1 + level,
						match: item,
						onclick: function () {
							frappe.new_doc(item, true);
						},
					});
				}
			});
		}
		return out;
	},

	get_doctypes: function (keywords) {
		var me = this;
		var out = [];

		var score, marked_string, target;
		var option = function (type, route, order) {
			// check to skip extra list in the text
			// eg. Price List List should be only Price List
			let skip_list = type === "List" && target.endsWith("List");
			if (skip_list) {
				var label = marked_string || __(target);
			} else {
				label = __(`{0} ${skip_list ? "" : type}`, [marked_string || __(target)]);
			}
			if (type === "List") {
				label = me.make_icon("list") + label; // @dokos
			} else if (type === "Report") {
				label = me.make_icon("table") + label; // @dokos
			} else {
				label = me.make_icon("search") + label; // @dokos
			}
			return {
				type: type,
				label: label,
				value: __(`{0} ${type}`, [target]),
				index: score + order,
				match: target,
				route: route,
			};
		};
		frappe.boot.user.can_read.forEach(function (item) {
			const search_result = me.fuzzy_search(keywords, item, true);
			({ score, marked_string } = search_result);
			if (score) {
				target = item;
				if (frappe.boot.single_types.includes(item)) {
					out.push(option("", ["Form", item, item], 0.05));
				} else if (frappe.boot.user.can_search.includes(item)) {
					// include 'making new' option
					if (frappe.boot.user.can_create.includes(item)) {
						var match = item;
						out.push({
							type: "New",
							label:
								me.make_icon("circle-plus") + // @dokos
								__("New {0}", [search_result.marked_string || __(item)]),
							value: __("New {0}", [__(item)]),
							index: score + 0.015,
							match: item,
							onclick: function () {
								frappe.new_doc(match, true);
							},
						});
					}

					const isTree = (frappe.boot.tree_view_doctypes || []).includes(item);
					let option_data = option(
						isTree ? "Tree" : "List",
						isTree ? ["Tree", item] : ["List", item],
						0.05
					);
					// A doctype reachable from several sidebars used to get one row per
					// sidebar, each pinning its own through `route_options`. They all
					// shared a single `value`, though, and Awesomplete resolves a selection
					// by value through a rotating cursor, so the sidebar you landed on was
					// never reliably the one you clicked -- and a sidebar naming the doctype
					// twice bought two rows that could not differ at all.
					out.push(option_data);
					if (frappe.model.can_get_report(item)) {
						out.push(option("Report", ["List", item, "Report"], 0.04));
					}
				}
			}
		});
		return out;
	},

	get_reports: function (keywords) {
		var me = this;
		var out = [];
		var route;
		Object.keys(frappe.boot.allowed_reports).forEach(function (item) {
			const search_result = me.fuzzy_search(keywords, item, true);
			var level = search_result.score;
			if (level > 0) {
				var report = frappe.boot.allowed_reports[item];
				if (report.report_type == "Report Builder")
					route = ["List", report.ref_doctype, "Report", item];
				else route = ["query-report", item];
				out.push({
					type: "Report",
					label:
						me.make_icon("grid-3x3") + // @dokos
						__("Report {0}", [search_result.marked_string || __(item)]),
					value: __("Report {0}", [__(item)]),
					index: level,
					route: route,
				});
			}
		});
		return out;
	},

	get_pages: function (keywords) {
		var me = this;
		var out = [];
		this.pages = {};
		$.each(frappe.boot.page_info, function (name, p) {
			me.pages[p.title] = p;
			p.name = name;
		});
		Object.keys(this.pages).forEach(function (item) {
			const search_result = me.fuzzy_search(keywords, item, true);
			var level = search_result.score;
			if (level) {
				var page = me.pages[item];
				out.push({
					type: "Page",
					label:
						me.make_icon("file-text") + // @dokos
						__("Open {0}", [search_result.marked_string || __(item)]),
					value: __("Open {0}", [__(item)]),
					match: item,
					index: level,
					route: [page.route || page.name],
				});
			}
		});
		var target = "Calendar";
		if (__("calendar").indexOf(keywords.toLowerCase()) === 0) {
			out.push({
				type: "Calendar",
				label: me.make_icon("calendar") + __("Open {0}", [__(target)]), // @dokos
				value: __("Open {0}", [__(target)]),
				index: me.fuzzy_search(keywords, "Calendar"),
				match: target,
				route: ["List", "Event", target],
			});
		}
		target = "Hub";
		if (__("hub").indexOf(keywords.toLowerCase()) === 0) {
			out.push({
				type: "Hub",
				label: me.make_icon("earth-lock") + __("Open {0}", [__(target)]), // @dokos
				value: __("Open {0}", [__(target)]),
				index: me.fuzzy_search(keywords, "Hub"),
				match: target,
				route: [target, "Item"],
			});
		}

		const inb = "Email Inbox"; // __("Calendar")
		const search_result_inbox = keywords ? me.fuzzy_search(keywords, __(inb)) : null;
		if (search_result_inbox?.score > 0) {
			out.push({
				type: "Inbox",
				label: me.make_icon("inbox") + __("Open {0}", [__("Email Inbox")]), // @dokos
				value: __("Open {0}", [__("Email Inbox")]),
				index: me.fuzzy_search(keywords, "email inbox"),
				match: target,
				route: ["List", "Communication", "Inbox"],
			});
		}

		return out;
	},

	// Search covers every workspace the user is permitted (`frappe.workspaces`), not just those
	// carrying sidebar items. That matters because a rail only lists the entries its app's `Dock`
	// record names, so for a workspace no record names, search is the way back to it.
	get_workspaces: function (keywords) {
		var me = this;
		var out = [];
		Object.values(frappe.workspaces || {}).forEach(function (workspace) {
			const name = workspace.name;
			const title = workspace.title || workspace.label || name;
			if (!title) return;

			const search_result = me.fuzzy_search(keywords, title, true);
			const level = search_result.score;
			if (level > 0) {
				out.push({
					type: "Workspace",
					label: __("Open {0} Workspace", [search_result.marked_string || __(title)]),
					value: __("Open {0} Workspace", [__(title)]),
					index: level,
					// open the workspace's sidebar and land on its first item; falls back to the
					// workspace's own route when it has no sidebar items
					onclick: function () {
						frappe.app.sidebar.open_workspace(name);
					},
				});
			}
		});
		return out;
	},

	get_dashboards: function (keywords) {
		var me = this;
		var out = [];
		frappe.boot.dashboards.forEach(function (item) {
			const search_result = me.fuzzy_search(keywords, item.name, true);
			var level = search_result.score;
			if (level > 0) {
				var ret = {
					type: "Dashboard",
					label:
						me.make_icon("layout-dashboard") + // @dokos
						__("{0} Dashboard", [search_result.marked_string || __(item.name)]),
					value: __("{0} Dashboard", [__(item.name)]),
					index: level,
					route: ["dashboard-view", item.name],
				};

				out.push(ret);
			}
		});
		return out;
	},

	get_global_results: function (keywords, start, limit, doctype = "") {
		var me = this;
		function get_results_sets(data) {
			var results_sets = [],
				result,
				set;
			function get_existing_set(doctype) {
				return results_sets.find(function (set) {
					return set.title === doctype;
				});
			}

			function make_description(content, doc_name) {
				var parts = content.split(" ||| ");
				var result_max_length = 300;
				var field_length = 120;
				var fields = [];
				var result_current_length = 0;
				var field_text = "";
				for (var i = 0; i < parts.length; i++) {
					var part = parts[i];
					if (part.toLowerCase().indexOf(keywords.toLowerCase()) !== -1) {
						// If the field contains the keyword
						let colon_index, field_value;
						if (part.indexOf(" &&& ") !== -1) {
							colon_index = part.indexOf(" &&& ");
							field_value = part.slice(colon_index + 5);
						} else {
							colon_index = part.indexOf(" : ");
							field_value = part.slice(colon_index + 3);
						}
						if (field_value.length > field_length) {
							// If field value exceeds field_length, find the keyword in it
							// and trim field value by half the field_length at both sides
							// ellipsify if necessary
							var field_data = "";
							var index = field_value.indexOf(keywords);
							field_data +=
								index < field_length / 2
									? field_value.slice(0, index)
									: "..." + field_value.slice(index - field_length / 2, index);
							field_data += field_value.slice(index, index + field_length / 2);
							field_data +=
								index + field_length / 2 < field_value.length ? "..." : "";
							field_value = field_data;
						}
						var field_name = part.slice(0, colon_index);

						// Find remaining result_length and add field length to result_current_length
						var remaining_length = result_max_length - result_current_length;
						result_current_length += field_name.length + field_value.length + 2;
						const search_result_name = me.fuzzy_search(keywords, field_name, true);
						const search_result_value = me.fuzzy_search(keywords, field_value, true);
						if (result_current_length < result_max_length) {
							// We have room, push the entire field
							field_text =
								'<span class="field-name text-muted">' +
								(search_result_name.marked_string || __(field_name)) +
								": </span> " +
								search_result_value.marked_string;
							if (fields.indexOf(field_text) === -1 && doc_name !== field_value) {
								fields.push(field_text);
							}
						} else {
							// Not enough room
							if (field_name.length < remaining_length) {
								// Ellipsify (trim at word end) and push
								remaining_length -= field_name.length;
								field_text =
									'<span class="field-name text-muted">' +
									(search_result_name.marked_string || __(field_name)) +
									": </span> ";
								field_value = field_value.slice(0, remaining_length);
								field_value =
									field_value.slice(0, field_value.lastIndexOf(" ")) + " ...";
								field_text += search_result_value.marked_string;
								fields.push(field_text);
							} else {
								// No room for even the field name, skip
								fields.push("...");
							}
							break;
						}
					}
				}
				return fields.join(", ");
			}

			data.forEach(function (d) {
				// more properties
				result = {
					label: d.title || d.name, // show title if exists
					value: d.name,
					description: make_description(d.content, d.name),
					route: ["Form", d.doctype, d.name],
				};
				if (d.image || d.image === null) {
					result.image = d.image;
				}
				set = get_existing_set(d.doctype);
				if (set) {
					set.results.push(result);
				} else {
					set = {
						title: d.doctype,
						results: [result],
						fetch_type: "Global",
					};
					results_sets.push(set);
				}
			});
			return results_sets;
		}
		return new Promise(function (resolve, reject) {
			frappe.call({
				method: "frappe.utils.global_search.search",
				args: {
					text: keywords,
					start: start,
					limit: limit,
					doctype: doctype,
				},
				callback: function (r) {
					if (r.message) {
						resolve(get_results_sets(r.message));
					} else {
						resolve([]);
					}
				},
			});
		});
	},

	get_nav_results: function (keywords) {
		function sort_uniques(options) {
			// @see deduplicate in frappe/ui/toolbar/awesome_bar.js
			var out = [],
				routes = [];
			options.forEach(function (option) {
				if (option.route) {
					let str_route =
						typeof option.route === "string" ? option.route : option.route.join("/");
					if (
						option.route[0] === "List" &&
						option.route[2] !== "Report" &&
						option.route[2] !== "Inbox"
					) {
						str_route = option.route.slice(0, 2).join("/");
					}

					if (routes.indexOf(str_route) === -1) {
						out.push(option);
						routes.push(str_route);
					} else {
						var old = routes.indexOf(str_route);
						if (out[old].index < option.index && !option.recent) {
							out[old] = option;
						}
					}
				} else {
					out.push(option);
					// routes.push("");
				}
			});
			return out.sort(function (a, b) {
				return b.index - a.index;
			});
		}
		var lists = [],
			setup = [];
		var all_doctypes = sort_uniques(this.get_doctypes(keywords));
		all_doctypes.forEach(function (d) {
			if (d.type === "") {
				setup.push(d);
			} else {
				lists.push(d);
			}
		});
		var in_keyword = keywords.split(" in ")[0];
		return [
			{
				title: __("Recents"),
				fetch_type: "Nav",
				results: sort_uniques(this.get_recent_pages(keywords)),
			},
			{
				title: __("Create a new ..."),
				fetch_type: "Nav",
				results: sort_uniques(this.get_creatables(keywords)),
			},
			{
				title: __("Lists"),
				fetch_type: "Nav",
				results: lists,
			},
			{
				title: __("Reports"),
				fetch_type: "Nav",
				results: sort_uniques(this.get_reports(keywords)),
			},
			{
				title: __("Administration"),
				fetch_type: "Nav",
				results: sort_uniques(this.get_pages(keywords)),
			},
			{
				title: __("Desktop Icon"),
				fetch_type: "Nav",
				results: sort_uniques(this.get_desktop_icons(keywords)),
			},
			{
				title: __("Dashboard"),
				fetch_type: "Nav",
				results: sort_uniques(this.get_dashboards(keywords)),
			},
			{
				title: __("Setup"),
				fetch_type: "Nav",
				results: setup,
			},
			{
				title: __("Find '{0}' in ... ", [in_keyword]),
				fetch_type: "Nav",
				results: sort_uniques(this.get_search_in_list(keywords)),
			},
		];
	},

	fuzzy_search: function (keywords = "", _item = "", return_marked_string = false) {
		let item = __(_item);

		let [, score, matches] = fuzzy_match(keywords, item, return_marked_string);
		if (score == 0 && frappe.boot.lang !== "en" && item != _item) {
			item = _item || "";
			[, score, matches] = fuzzy_match(keywords, item, return_marked_string);
		}

		if (!return_marked_string) {
			return score;
		}
		if (score == 0) {
			return {
				score: score,
				marked_string: item,
			};
		}

		// Create Boolean mask to mark matching indices in the item string
		const matchArray = Array(item.length).fill(0);
		matches.forEach((index) => (matchArray[index] = 1));

		let marked_string = "";
		let buffer = "";

		// Clear the buffer and return marked matches.
		const flushBuffer = () => {
			if (!buffer) return "";
			const temp = `<mark>${buffer}</mark>`;
			buffer = "";
			return temp;
		};

		matchArray.forEach((isMatch, index) => {
			if (isMatch) {
				buffer += item[index];
			} else {
				marked_string += flushBuffer();
				marked_string += item[index];
			}
		});
		marked_string += flushBuffer();

		return { score, marked_string };
	},

	/**
	 * @deprecated Use frappe.search.utils.fuzzy_search(subseq, str, true).marked_string instead.
	 */
	bolden_match_part: function (str, subseq) {
		return this.fuzzy_search(subseq, str, true).marked_string;
	},

	get_executables(keywords) {
		let results = [];
		this.searchable_functions.forEach((item) => {
			const target = item.label.toLowerCase();
			const txt = keywords.toLowerCase();
			if (txt === target || target.indexOf(txt) === 0) {
				const search_result = this.fuzzy_search(txt, item.label, true);
				results.push({
					type: "Executable",
					value: search_result.marked_string,
					index: search_result.score,
					match: item.label,
					onclick: () => item.action.apply(this, item.args),
				});
			}
		});
		return results;
	},
	make_function_searchable(_function, label = null, args = null) {
		if (typeof _function !== "function") {
			throw new Error("First argument should be a function");
		}

		this.searchable_functions.push({
			label: label || _function.name,
			action: _function,
			args: args,
		});
	},
	searchable_functions: [],
};

// @dokos: ranks results by how much and how recently this user visits them
frappe.search.frecency = {
	scores: null,
	refreshed: false,

	links() {
		return frappe.boot.frequently_visited_links || [];
	},

	// Boot is cached without expiry, so its scores would never decay.
	refresh() {
		if (this.refreshed) return;
		this.refreshed = true;
		// Silent: focusing the search box must not pop an error. The next focus retries.
		frappe
			.xcall(
				"frappe.desk.doctype.route_history.route_history.frequently_visited_links",
				{},
				"POST",
				{ silent: true }
			)
			.then((links) => {
				frappe.boot.frequently_visited_links = links;
				this.load();
			})
			.catch((error) => {
				this.refreshed = false;
				console.warn("Could not refresh the awesome bar's visit history", error);
			});
	},

	// Route History stores "List/Item/List" where options carry ["List", "Item"].
	route_key(route) {
		const parts = typeof route === "string" ? route.split("/") : route;
		const is_list_view = parts[0] === "List" && !["Report", "Inbox"].includes(parts[2]);
		return (is_list_view ? parts.slice(0, 2) : parts).join("/");
	},

	load() {
		this.scores = {};
		this.links().forEach((link) => {
			const key = this.route_key(link.route);
			this.scores[key] = (this.scores[key] || 0) + link.score;
		});
		return this.scores;
	},

	score_of(option) {
		if (!this.scores) this.load();
		// "New <doctype>" has no route; its doctype is in `match`.
		if (option.type === "New") return this.scores[NEW_DOCUMENT_KEY_PREFIX + option.match] || 0;
		const route = option.frecency_route || option.route;
		return route ? this.scores[this.route_key(route)] || 0 : 0;
	},

	// Reorders near-ties only, by swapping their scores: Awesomplete sorts by `index`.
	rerank(options) {
		if (!options.length || options[0].index <= 0) return;

		const top_score = options[0].index;
		const cutoff =
			top_score > FUZZY_BASE_SCORE
				? Math.max(top_score * FRECENCY_BAND, FUZZY_BASE_SCORE)
				: top_score * FRECENCY_BAND;
		const near_ties = options.filter((option) => option.index >= cutoff);
		const scores_to_share = near_ties.map((option) => option.index);
		// Compared by rank: moving past a duplicate score gains no score.
		const rank_before = new Map(near_ties.map((option, rank) => [option, rank]));

		near_ties
			.sort((a, b) => this.score_of(b) - this.score_of(a) || b.index - a.index)
			.forEach((option, rank) => {
				option.boosted_by_history = rank < rank_before.get(option);
				option.index = scores_to_share[rank] + (near_ties.length - rank) * RANK_EPSILON;
			});
	},
};

// @dokos: remembers the result picked for a query, on this device only.
frappe.search.memory = {
	normalize(query) {
		return (query || "").trim().toLowerCase().replace(/\s\s+/g, " ");
	},

	// Per user: a browser profile can be shared.
	storage_key() {
		return `${MEMORY_KEY}:${frappe.session.user}`;
	},

	// recall runs on every keystroke.
	cache: null,

	load() {
		if (this.cache) return this.cache;
		try {
			this.cache = JSON.parse(localStorage.getItem(this.storage_key())) || {};
		} catch (e) {
			// corrupted storage only costs relearning a few picks
			this.cache = {};
		}
		return this.cache;
	},

	// Laplace-smoothed pick rate, faded by how long the query has gone unused.
	confidence(entry) {
		const weight = this.fade(entry);
		return (entry.hits * weight + 1) / ((entry.hits + entry.misses) * weight + 2);
	},

	fade(entry) {
		const idle_days = entry.last_used
			? (Date.now() - entry.last_used) / (24 * 60 * 60 * 1000)
			: 0;
		return 0.5 ** (idle_days / MEMORY_HALF_LIFE_DAYS);
	},

	record(query, value) {
		const normalized_query = this.normalize(query);
		if (normalized_query.length < 2) return;

		const memory = this.load();
		const stored_key = MEMORY_KEY_PREFIX + normalized_query;
		const entry = memory[stored_key];
		let updated;

		// Faded first: the write resets last_used, which would revive an expired pin.
		const weight = entry ? this.fade(entry) : 1;
		const hits = entry ? entry.hits * weight : 0;
		const misses = entry ? entry.misses * weight : 0;

		if (!entry || (entry.value !== value && misses + 1 >= hits)) {
			updated = { value: value, hits: 1, misses: 0 };
		} else if (entry.value === value) {
			updated = { value: value, hits: hits + 1, misses: misses };
		} else {
			updated = { value: entry.value, hits: hits, misses: misses + 1 };
		}

		// Reinserted so the eviction below drops the least recently used query.
		delete memory[stored_key];
		memory[stored_key] = { ...updated, last_used: Date.now() };

		const queries = Object.keys(memory);
		if (queries.length > MEMORY_MAX_QUERIES) delete memory[queries[0]];

		try {
			localStorage.setItem(this.storage_key(), JSON.stringify(memory));
		} catch (e) {
			// storage full or disabled: the boost is optional
		}
	},

	recall(query) {
		const normalized_query = this.normalize(query);
		const memory = this.load();

		// Longest trusted prefix: picks are usually recorded on a shorter query.
		for (let length = normalized_query.length; length >= 2; length--) {
			const entry = memory[MEMORY_KEY_PREFIX + normalized_query.slice(0, length)];
			if (entry && this.confidence(entry) > MEMORY_MIN_CONFIDENCE) {
				return entry.value;
			}
		}
		return null;
	},

	// `rivals` are results already on the list, like "Search for …".
	pin(options, query, rivals = []) {
		const remembered = this.recall(query);
		if (!remembered) return;

		// Every copy: deduplicate keeps one per source for options with a description.
		const pinned = options.filter((option) => option.value === remembered);
		if (!pinned.length) return;

		const top_index = Math.max(...options.concat(rivals).map((option) => option.index)) + 1;
		pinned.forEach((option) => {
			option.index = top_index;
			option.boosted_by_history = true;
			option.pinned_for_query = true;
		});
	},
};

/** Closes the navbar Awesome Bar modal. */
function hide_navbar_search_modal() {
	const $modal = $("#navbar-search").closest(".modal");
	if ($modal.length) $modal.modal("hide");
}

/**
 * Open the global search dialog from the navbar Awesome Bar (Ctrl/Cmd+G).
 */
frappe.search.open_global_search_from_navbar_shortcut = function (e) {
	const from_bar = ($("#navbar-search").val() || "").trim();
	const dlg = frappe.searchdialog?.search;
	if (dlg?.toggle_global_search_dialog) {
		hide_navbar_search_modal();
		dlg.toggle_global_search_dialog(from_bar);
	}
	if (e) {
		e.preventDefault();
	}
	return false;
};

/**
 * Open the navbar Awesome Bar from Global Search (Ctrl/Cmd+K).
 */
frappe.search.open_awesomebar_from_global_search_shortcut = function (e) {
	if (e) {
		e.preventDefault();
	}
	const awesome_bar = frappe.app.awesome_bar;
	if (awesome_bar?.is_open()) {
		awesome_bar.close();
		return false;
	}
	const dlg = frappe.searchdialog?.search;
	if (dlg?.search_dialog?.is_visible) {
		const keywords = (dlg.$input?.val() || "").trim();
		dlg.search_dialog.hide();
		$("#navbar-search").val(keywords);
	}
	awesome_bar.open();
	return false;
};
